import React from "react";
import {Controller, useFieldArray, useFormContext, useWatch} from "react-hook-form";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {Box, Colors, Container, IconButton, IconTrash, Text} from "@bounce/shared/ui";
import {Button, MenuItem, Select, TextField} from "../../../ui/components";
import {PromoTermType, PromoTermUnit} from "../../../apis/requests/promos/types";
import {FormCard, INPUT_SX} from "./FormCard";
import {
  ALLOWED_PLAN_TYPES_BY_TERM_TYPE,
  ALLOWED_UNITS_BY_TERM_TYPE,
  APPLIES_TO_LABELS,
  APPLIES_TO_OPTIONS,
  AppliesToOption,
  FIXED_UNIT_LABELS,
  PLAN_TYPE_LABELS,
  PROMO_TERM_TYPES,
  SELECTABLE_UNIT_LABELS,
  TERM_TYPE_LABELS,
  UNITS_WITHOUT_AMOUNT,
} from "./constants";
import {appliesToOption, newPromoTerm, nextAvailableTerm, parseNumberInput, planTypesForOption} from "./helpers";
import {PromoFormValues} from "./schema";

const SA_CAP_NOTICE =
  "Every resolved offer is capped to the partner’s settlement authority. Terms above authority for any partner in this audience cannot be saved.";
const NO_TERM_LEFT_HINT =
  "Every term is in use. To use a different term, change an existing term's type or plan, or remove it.";
const NOTICE_ICON_SIZE = 18;
const NOT_APPLICABLE = "—";
const VALUE_INPUT_WIDTH = 100;
const UNIT_INPUT_WIDTH = 190;
const COLUMN_HEADERS = ["Term type", "Value", "Unit", "Applies to"];

const ColumnLabel = ({children, sx}: {children: string; sx?: object}) => (
  <Text size={12} sx={{color: Colors.grey._700, ...sx}}>
    {children}
  </Text>
);

const TermsCard = () => {
  const {control, setValue} = useFormContext<PromoFormValues>();
  const {fields, append, remove, update} = useFieldArray({control, name: "terms"});
  const terms = useWatch({control, name: "terms"});
  const nextTerm = nextAvailableTerm(terms ?? []);

  const onTypeChange = (index: number, type: PromoTermType) => update(index, newPromoTerm(type));

  const onUnitChange = (index: number, unit: PromoTermUnit) => {
    setValue(`terms.${index}.unit`, unit, {shouldDirty: true});
    if (UNITS_WITHOUT_AMOUNT.has(unit)) setValue(`terms.${index}.amount`, null, {shouldDirty: true});
  };

  return (
    <FormCard title="Terms">
      <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
        <Container row fullWidth sx={{gap: 1.5, flexWrap: "nowrap", pr: 5}}>
          <ColumnLabel sx={{flex: 2}}>{COLUMN_HEADERS[0]}</ColumnLabel>
          <ColumnLabel sx={{width: VALUE_INPUT_WIDTH}}>{COLUMN_HEADERS[1]}</ColumnLabel>
          <ColumnLabel sx={{width: UNIT_INPUT_WIDTH}}>{COLUMN_HEADERS[2]}</ColumnLabel>
          <ColumnLabel sx={{flex: 2}}>{COLUMN_HEADERS[3]}</ColumnLabel>
        </Container>
        {fields.map((field, index) => {
          const term = terms?.[index] ?? field;
          const allowedUnits = ALLOWED_UNITS_BY_TERM_TYPE[term.type];
          const allowedPlanTypes = ALLOWED_PLAN_TYPES_BY_TERM_TYPE[term.type];
          const takesAmount = !UNITS_WITHOUT_AMOUNT.has(term.unit);
          return (
            <Container key={field.id} row fullWidth sx={{gap: 1.5, alignItems: "center", flexWrap: "nowrap"}}>
              <Box sx={{flex: 2, minWidth: 0}}>
                <Controller
                  name={`terms.${index}.type`}
                  control={control}
                  render={({field: typeField, fieldState}) => (
                    <Select
                      {...typeField}
                      onChange={(event) => onTypeChange(index, event.target.value as PromoTermType)}
                      fullWidth
                      size="small"
                      error={!!fieldState.error}
                      sx={INPUT_SX}
                    >
                      {PROMO_TERM_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {TERM_TYPE_LABELS[type]}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Box>
              <Box sx={{width: VALUE_INPUT_WIDTH}}>
                {takesAmount ? (
                  <Controller
                    name={`terms.${index}.amount`}
                    control={control}
                    render={({field: {ref, onChange, value, ...amountField}, fieldState}) => (
                      <TextField
                        {...amountField}
                        inputRef={ref}
                        type="number"
                        size="small"
                        value={value ?? ""}
                        onChange={(event) => onChange(parseNumberInput(event.target.value))}
                        error={!!fieldState.error}
                        sx={INPUT_SX}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    disabled
                    fullWidth
                    size="small"
                    value=""
                    placeholder={NOT_APPLICABLE}
                    sx={{...INPUT_SX, backgroundColor: Colors.grey._100}}
                  />
                )}
              </Box>
              <Box sx={{width: UNIT_INPUT_WIDTH}}>
                {allowedUnits.length > 1 ? (
                  <Controller
                    name={`terms.${index}.unit`}
                    control={control}
                    render={({field: unitField, fieldState}) => (
                      <Select
                        {...unitField}
                        onChange={(event) => onUnitChange(index, event.target.value as PromoTermUnit)}
                        fullWidth
                        size="small"
                        error={!!fieldState.error}
                        sx={INPUT_SX}
                      >
                        {allowedUnits.map((unit) => (
                          <MenuItem key={unit} value={unit}>
                            {SELECTABLE_UNIT_LABELS[unit]}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                ) : (
                  <Text size={14} sx={{color: Colors.grey._700}}>
                    {FIXED_UNIT_LABELS[term.type]}
                  </Text>
                )}
              </Box>
              <Box sx={{flex: 2, minWidth: 0}}>
                {allowedPlanTypes.length > 1 ? (
                  <Controller
                    name={`terms.${index}.plan_types`}
                    control={control}
                    render={({field: planTypesField, fieldState}) => (
                      <Select
                        name={planTypesField.name}
                        inputRef={planTypesField.ref}
                        onBlur={planTypesField.onBlur}
                        value={appliesToOption(planTypesField.value)}
                        onChange={(event) =>
                          planTypesField.onChange(planTypesForOption(event.target.value as AppliesToOption))
                        }
                        fullWidth
                        size="small"
                        error={!!fieldState.error}
                        sx={INPUT_SX}
                      >
                        {APPLIES_TO_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {APPLIES_TO_LABELS[option]}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                ) : (
                  <Text size={14} sx={{color: Colors.grey._700}}>
                    {PLAN_TYPE_LABELS[allowedPlanTypes[0]]}
                  </Text>
                )}
              </Box>
              <IconButton
                onClick={() => remove(index)}
                disabled={fields.length === 1}
                size="small"
                sx={{flexShrink: 0, color: Colors.grey._500, "&:hover": {backgroundColor: Colors.tertiary._200}}}
              >
                <IconTrash width={18} height={18} color="currentColor" />
              </IconButton>
            </Container>
          );
        })}
      </Box>
      <Container row sx={{gap: 1.5, alignItems: "center"}}>
        <Button
          buttonType="outlined-primary"
          size="small"
          disabled={!nextTerm}
          onClick={() => nextTerm && append(nextTerm)}
          sx={{backgroundColor: Colors.bw.white}}
        >
          + Add term
        </Button>
        {!nextTerm && (
          <Text size={12} sx={{color: Colors.grey._700}}>
            {NO_TERM_LEFT_HINT}
          </Text>
        )}
      </Container>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          p: "10px 12px",
          borderRadius: "8px",
          border: `1px solid ${Colors.orange._200}`,
          backgroundColor: Colors.orange._100,
        }}
      >
        <WarningAmberRoundedIcon sx={{fontSize: NOTICE_ICON_SIZE, color: Colors.orange._900, mt: "1px"}} />
        <Text size={13} sx={{color: Colors.orange._1000, lineHeight: 1.5}}>
          {SA_CAP_NOTICE}
        </Text>
      </Box>
    </FormCard>
  );
};

export default TermsCard;
